import type { ReactElement } from 'react';

import { Skeleton } from '@/components/atoms/motion/Skeleton';

/**
 * Full-page loading skeleton for the event detail page.
 * Matches the final layout: hero → title/meta → action rail → ticket CTA →
 * info sections → artist → related events. Used as a Suspense fallback in
 * `app/(city)/event/[slug]/page.tsx`.
 *
 * Built on the DS 1.4.0 `Skeleton` motion atom (glass shimmer). The atom sets
 * inline `width`/`height` defaults that override Tailwind className sizing, so:
 *
 * - Fixed-size leaves pass `width`/`height`/`radius` props directly. Fractional
 *   widths are also props — the atom accepts any CSS length (`width="50%"`).
 * - The two elements with breakpoint-prefixed dimensions (the hero and the
 *   title, which can't be expressed as props) use the fill pattern: a
 *   `relative overflow-hidden` wrapper that carries the responsive height
 *   classes verbatim, with an absolute-fill `Skeleton` inside. This is exactly
 *   `HomeFeedSkeleton`'s vocabulary (WEB-463 slice 1).
 *
 * The atom is decorative (`aria-hidden`) and server-safe (no client hooks):
 * this wrapper owns the single `role="status"` + sr-only label so assistive
 * tech announces "Loading event details" exactly once, and reduced-motion is
 * handled centrally in `loci-motion.css` (`.lm-sk::after` settles).
 */
export function EventDetailSkeleton(): ReactElement {
  return (
    <div className="min-h-screen bg-background" role="status" aria-label="Loading event details">
      <span className="sr-only">Loading event details</span>

      {/* Hero image — breakpoint-prefixed height → fill pattern */}
      <div className="relative overflow-hidden w-full h-[250px] tablet:h-[350px] desktop:h-[400px]">
        <Skeleton
          variant="block"
          radius={0}
          className="absolute inset-0"
          width="100%"
          height="100%"
        />
      </div>

      <div className="container mx-auto px-4 tablet:px-6 desktop:px-8 py-6">
        <div className="max-w-4xl mx-auto flex flex-col gap-8">
          {/* Title + meta */}
          <div className="flex flex-col gap-3">
            {/* Title — breakpoint-prefixed height → fill pattern */}
            <div className="relative overflow-hidden h-8 w-3/4 tablet:h-10">
              <Skeleton
                variant="block"
                radius={6}
                className="absolute inset-0"
                width="100%"
                height="100%"
              />
            </div>
            <Skeleton variant="line" width="50%" height={20} />
            <div className="flex flex-wrap gap-2 mt-1">
              <Skeleton variant="block" width={96} height={24} radius={9999} />
              <Skeleton variant="block" width={80} height={24} radius={9999} />
            </div>
          </div>

          {/* Action rail — row of circular buttons */}
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="block" width={44} height={44} radius={9999} />
            ))}
          </div>

          {/* Ticket CTA placeholder */}
          <Skeleton
            variant="block"
            width="100%"
            height={56}
            radius="var(--r-lg)"
            className="w-full"
          />

          {/* Info accordion sections */}
          <div className="flex flex-col gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 py-3">
                <Skeleton
                  variant="block"
                  width={20}
                  height={20}
                  radius={4}
                  className="shrink-0 mt-0.5"
                />
                <div className="flex flex-col gap-2 flex-1">
                  <Skeleton variant="line" width="33.333%" height={20} />
                  <Skeleton variant="line" width="100%" height={16} />
                  <Skeleton variant="line" width="66.667%" height={16} />
                </div>
              </div>
            ))}
          </div>

          {/* Artist section */}
          <div className="flex flex-col gap-3">
            <Skeleton variant="line" width={96} height={24} />
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-2 shrink-0">
                  <Skeleton variant="block" width={64} height={64} radius={9999} />
                  <Skeleton variant="line" width={80} height={16} />
                </div>
              ))}
            </div>
          </div>

          {/* Related events */}
          <div className="flex flex-col gap-3">
            <Skeleton variant="line" width={128} height={24} />
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="block"
                  width={280}
                  height={200}
                  radius="var(--r-lg)"
                  className="shrink-0"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
